import { useState } from 'react';
import { Link } from 'react-router';
import { tx } from '../i18n';
import { pedir } from '../lib/api';
import { permisos } from '../lib/appliance';
import { useConsulta } from '../lib/consulta';
import { miles } from '../lib/formato';
import { useSesion } from '../sesion/sesion';
import type { Organizacion } from '../tipos';
import { Cabecera, Consulta, primero, Tabla, useAviso, useRefrescar, Vacio, Ventana } from '../ui/piezas';

/** Alta y edición comparten formulario: cambian el título, el botón y adónde se manda. */
function FormOrganizacion({ org, alCerrar, alListo }: { org: Organizacion | null; alCerrar: () => void; alListo: (n: string) => void }) {
  const [nombre, setNombre] = useState(org?.nombre ?? '');
  const [contacto, setContacto] = useState(org?.contacto ?? '');
  const [notas, setNotas] = useState(org?.notas ?? '');
  return (
    <Ventana
      titulo={org ? tx.organizaciones.editarA(org.nombre) : tx.organizaciones.nueva}
      boton={org ? 'Save' : 'Create'}
      alCerrar={alCerrar}
      alEnviar={async () => {
        const falta = primero(!nombre.trim() && tx.organizaciones.faltaNombre);
        if (falta) return falta;
        const cuerpo = { nombre: nombre.trim(), contacto: contacto.trim() || null, notas: notas.trim() || null };
        if (org) await pedir(`/organizaciones/${org.id}`, { metodo: 'PATCH', cuerpo });
        else await pedir('/organizaciones', { metodo: 'POST', cuerpo });
        alListo(nombre.trim());
      }}
    >
      <label className="campo" htmlFor="org-nombre">
        {tx.comun.nombre}
        <input id="org-nombre" className="entrada" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder={tx.organizaciones.nombreEjemplo} />
      </label>
      <label className="campo" htmlFor="org-contacto">
        {tx.comun.contacto}
        <input
          id="org-contacto"
          className="entrada"
          value={contacto}
          onChange={(e) => setContacto(e.target.value)}
          placeholder={tx.organizaciones.contactoEjemplo}
        />
      </label>
      <label className="campo" htmlFor="org-notas">
        {tx.comun.notas}
        <textarea
          id="org-notas"
          className="entrada"
          rows={3}
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
          placeholder={tx.organizaciones.notasEjemplo}
        />
      </label>
    </Ventana>
  );
}

export function Organizaciones() {
  const { usuario } = useSesion();
  const puede = permisos(usuario?.rol);
  const [aviso, avisar] = useAviso();
  const refrescar = useRefrescar();
  const [nueva, setNueva] = useState(false);
  const [editando, setEditando] = useState<Organizacion | null>(null);

  const lista = useConsulta<Organizacion[]>('/organizaciones');
  const filas = lista.data ?? [];

  return (
    <div className="pagina">
      <Cabecera
        titulo={tx.organizaciones.titulo}
        accion={
          puede.crearOrganizacion && (
            <button type="button" className="btn prim" onClick={() => setNueva(true)}>
              {tx.organizaciones.nueva}
            </button>
          )
        }
      >
        {tx.organizaciones.que}
      </Cabecera>
      {aviso}

      <section className="panel">
        <Consulta
          q={lista}
          vacio={
            lista.data && filas.length === 0 ? <Vacio>{tx.organizaciones.ninguna}</Vacio> : undefined
          }
        >
          <div className="scroll-x">
            <Tabla>
              <thead>
                <tr>
                  <th>{tx.comun.organizacion}</th>
                  <th>{tx.comun.contacto}</th>
                  <th>{tx.comun.notas}</th>
                  <th className="der">{tx.comun.appliances}</th>
                  {puede.editarOrganizacion && <th />}
                </tr>
              </thead>
              <tbody>
                {filas.map((o) => (
                  <tr key={o.id}>
                    <td>
                      <b>{o.nombre}</b>
                    </td>
                    <td className="d">{o.contacto || '—'}</td>
                    <td className="d notas-celda">{o.notas || '—'}</td>
                    <td className="der num">{o.appliances > 0 ? <Link to={`/appliances?organizacion=${o.id}`}>{miles(o.appliances)}</Link> : '0'}</td>
                    {puede.editarOrganizacion && (
                      <td className="acciones der">
                        <button type="button" className="btn chico" onClick={() => setEditando(o)}>
                          {tx.comun.editar}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Tabla>
          </div>
        </Consulta>
      </section>

      {nueva && (
        <FormOrganizacion
          org={null}
          alCerrar={() => setNueva(false)}
          alListo={(n) => {
            setNueva(false);
            void refrescar('/organizaciones', '/resumen', '/bitacora');
            avisar(tx.organizaciones.creada(n));
          }}
        />
      )}
      {editando && (
        <FormOrganizacion
          org={editando}
          alCerrar={() => setEditando(null)}
          alListo={(n) => {
            setEditando(null);
            void refrescar('/organizaciones', '/appliances', '/bitacora');
            avisar(tx.organizaciones.guardada(n));
          }}
        />
      )}
    </div>
  );
}
